import { Observable, catchError, map, of } from 'rxjs'
import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'

import { environment } from '@env/environment'

/** Una cuenta desde la que el cliente transfiere. */
export interface ClientBankAccount {
  _id: string
  clabe: string
  bank: string | null
  holderName: string | null
  client: string
  /** `MANUAL` capturada a mano · `IMPORT` de una carga masiva · `OBSERVED` deducida de un depósito. */
  source: 'MANUAL' | 'IMPORT' | 'OBSERVED'
  active: boolean
  createdAt?: string
}

export interface NuevaCuenta {
  clabe: string
  client: string
  bank?: string | null
  holderName?: string | null
}

type Wrapped<T> = { error: boolean; msg: string; data: T | null }

/**
 * Las cuentas bancarias **de los clientes**: aquellas desde las que transfieren.
 *
 *! No confundir con `accounts`, que son las cuentas de la arrendadora —Santander, Covalto, Bajío— a
 *! las que cae el dinero. Aquí se guarda lo contrario: el ORIGEN. Es lo que permite reconocer un
 *! depósito que llega sin referencia, y hoy ese dato no existe en ningún lado del sistema.
 *
 * Las rutas van con `payments:reconcile`, no con `get-all`: exponen la CLABE de un cliente.
 */
@Injectable({ providedIn: 'root' })
export class ApiClientBankAccountsService {
  private readonly http = inject(HttpClient)

  /** Las cuentas de un cliente, activas e inactivas. */
  findByClient(clientId: string): Observable<Wrapped<ClientBankAccount[]>> {
    return this.http.get(`${environment.apiUrl}payments/bank-accounts/${clientId}`).pipe(
      map((r) => r as Wrapped<ClientBankAccount[]>),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudieron cargar las cuentas', data: null })),
    )
  }

  /** Alta de una cuenta. Una CLABE pertenece a un solo cliente: si ya es de otro, el servidor la rechaza. */
  create(dto: NuevaCuenta): Observable<Wrapped<ClientBankAccount>> {
    return this.http.post(`${environment.apiUrl}payments/bank-accounts`, dto).pipe(
      map((r) => r as Wrapped<ClientBankAccount>),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudo registrar la cuenta', data: null })),
    )
  }

  /** Baja lógica: los movimientos viejos siguen necesitando la cuenta para explicarse. */
  deactivate(id: string): Observable<Wrapped<ClientBankAccount>> {
    return this.http.delete(`${environment.apiUrl}payments/bank-accounts/${id}`).pipe(
      map((r) => r as Wrapped<ClientBankAccount>),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudo desactivar la cuenta', data: null })),
    )
  }
}
